import type { ModuleID, VersionString } from "@bestowinc/tinkertoy-core";

import type {
  LoadedModule,
  ModuleLoader,
  ModuleLoadError,
} from "../module-loader";

// ============================================================================
// Deduped loads (one promise per moduleId + version)
// ============================================================================

function makeKey(moduleId: ModuleID, version: VersionString): string {
  return `${moduleId}:${version}`;
}

// Loading modules are loaded with the `allowLoadingModule` opt-in, so they get
// their own key space rather than sharing a promise loaded without it.
function makeLoadingKey(moduleId: ModuleID, version: VersionString): string {
  return `loading:${moduleId}:${version}`;
}

const loadPromises = new Map<string, Promise<LoadedModule>>();
const loaded = new Map<string, LoadedModule>();

function getOrLoad(
  key: string,
  load: () => Promise<LoadedModule>,
): Promise<LoadedModule> {
  let p = loadPromises.get(key);
  if (!p) {
    p = load();
    const pending = p;
    // Evict failed loads so transient errors (CDN blips, network) don't
    // permanently brick the module for the rest of the session. Identity-guard
    // the delete so a late-firing rejection can't clobber a fresh promise that
    // was inserted under the same key after a cache reset.
    void pending.then(
      result => {
        if (loadPromises.get(key) === pending) {
          loaded.set(key, result);
        }
      },
      () => {
        if (loadPromises.get(key) === pending) {
          loadPromises.delete(key);
        }
      },
    );
    loadPromises.set(key, p);
  }
  return p;
}

/** Same cached promise for preload and subscribers — use from effects (no Suspense). */
export function getModuleLoadPromise(
  loader: ModuleLoader,
  moduleId: ModuleID,
  version: VersionString,
): Promise<LoadedModule> {
  return getOrLoad(makeKey(moduleId, version), () =>
    loader.loadModule(moduleId, version),
  );
}

/**
 * Deduped load for a `"loading"` module (passes the loader's opt-in). Rejects
 * any other module type as `INVALID_MODULE`, since the loading host can't
 * supply their actions, data, slots or children.
 */
export function getLoadingModuleLoadPromise(
  loader: ModuleLoader,
  moduleId: ModuleID,
  version: VersionString,
): Promise<LoadedModule> {
  return getOrLoad(makeLoadingKey(moduleId, version), async () => {
    const result = await loader.loadModule(moduleId, version, {
      allowLoadingModule: true,
    });
    if (result.definition.type !== "loading") {
      const error: ModuleLoadError = {
        moduleId,
        version,
        cdnUrl: result.cdnUrl,
        code: "INVALID_MODULE",
        reason: `Module "${moduleId}" has type "${result.definition.type}" and cannot be rendered as a loading module.`,
      };
      throw error;
    }
    return result;
  });
}

/**
 * The already-loaded module for `moduleId@version`, if its load has settled.
 * Lets renderers start in the ready state instead of rendering a blank frame
 * for a bundle that is already cached.
 */
export function peekLoadedModule(
  moduleId: ModuleID,
  version: VersionString,
): LoadedModule | undefined {
  return loaded.get(makeKey(moduleId, version));
}

/** {@link peekLoadedModule} for loading modules. */
export function peekLoadedLoadingModule(
  moduleId: ModuleID,
  version: VersionString,
): LoadedModule | undefined {
  return loaded.get(makeLoadingKey(moduleId, version));
}

/**
 * Clears the module load dedupe cache.
 *
 * @remarks Not part of the supported public API for apps — tests (and rare dev tooling)
 * import this to reset module-scope state between cases.
 *
 * @internal
 */
export function resetModuleReadResourceCache(): void {
  loadPromises.clear();
  loaded.clear();
}
